import React from "react";
import { MultiSelectDropdownWithField, MultiSelectOption } from "@/components/ui/dropdowns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/user-feedback/tooltip";
import { getCachedDialogs } from "@/lib/stores/dialogStore";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";

interface DialogSelectionElementProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  onBlur?: (value: string | string[]) => void;
  placeholder?: string;
  multiple?: boolean;
  filterOptions?: string[]; // Array of tags to filter by
  disabled?: boolean;
}

export const DialogSelectionElement: React.FC<DialogSelectionElementProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = "Select dialogs...",
  multiple = false,
  filterOptions = [],
  disabled = false,
}) => {
  // Convert string value to array for multiple mode, keep as-is for single mode
  const dropdownValue = multiple && typeof value === 'string' 
    ? (value ? value.split(',') : [])
    : value;

  // Handle onChange conversion: array -> string for multiple, keep as-is for single
  const handleDropdownChange = (newValue: string | string[]) => {
    if (multiple && Array.isArray(newValue)) {
      // Convert array to comma-separated string
      onChange(newValue.join(','));
    } else {
      // Single mode or already a string
      onChange(newValue);
    }
  };

  // Handle onBlur conversion
  const handleDropdownBlur = (newValue: string | string[]) => {
    if (onBlur) {
      if (multiple && Array.isArray(newValue)) {
        // Convert array to comma-separated string
        onBlur(newValue.join(','));
      } else {
        // Single mode or already a string
        onBlur(newValue);
      }
    }
  };

  const { selectedGame } = useSimplifiedUserData();
  
  // Get all dialogs for the current game
  const allDialogs = selectedGame?.id ? getCachedDialogs(selectedGame.id) : [];
  
  // Filter dialogs based on filterOptions (tags) if filter is enabled
  const availableDialogs = filterOptions.length > 0 
    ? allDialogs.filter(dialog => 
        // Check if the dialog has at least one of the specified tags
        dialog.tags && dialog.tags.some(tag => filterOptions.includes(tag))
      )
    : allDialogs;

  // Check if we should disable the dropdown
  const hasNoAvailableDialogs = availableDialogs.length === 0;
  
  // Only disable if there are no available dialogs, not when there are no filter options
  // When there are no filter options, we should show all available dialogs
  const shouldDisable = hasNoAvailableDialogs;
  const finalDisabled = disabled || shouldDisable;

  // Convert dialogs to MultiSelectOption format
  const dialogOptions: MultiSelectOption[] = availableDialogs.map(dialog => ({
    id: dialog.id,
    label: dialog.name,
    description: `Key: ${dialog.dialog_key || 'N/A'} | Tags: ${dialog.tags?.join(', ') || 'None'}`,
    parentTypeId: 'dialog',
    parentTypeName: 'Dialog'
  }));

  // Create the dropdown component
  const dropdownComponent = (
    <MultiSelectDropdownWithField
      value={dropdownValue}
      onChange={handleDropdownChange}
      onBlur={handleDropdownBlur}
      options={dialogOptions}
      placeholder={placeholder}
      multiple={multiple}
      disabled={finalDisabled}
      isLoading={false}
    />
  );

  // If we should disable due to no options, wrap with tooltip
  if (shouldDisable) {
    return (
      <TooltipProvider delayDuration={100} skipDelayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            {dropdownComponent}
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {filterOptions.length > 0 
                ? "No dialogs available with the specified tags. Please check your filter options or create dialogs with the required tags."
                : "No dialogs available. Please create some dialogs first."
              }
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Otherwise return the dropdown without tooltip
  return dropdownComponent;
};















